// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/Develop.tsx

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Commit as CommitIcon,
  CallSplit as CallSplitIcon,
  BugReport as BugReportIcon,
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import ThemeAwareMarkdown from './ThemeAwareMarkdown'; // Import the new component

import { API_BASE_URL } from './api';

// Define the shape of the state object that will be passed from App.tsx
export interface DevelopState {
  repoUrl: string;
  githubToken: string;
  repoData: any;
  commits: any[];
  pulls: any[];
  issues: any[];
  commitAnalysis: string;
  error: string | null;
  isLoading: boolean;
}

interface DevelopProps {
  developState: DevelopState;
  setDevelopState: React.Dispatch<React.SetStateAction<DevelopState>>;
  onGoBack: () => void;
  onResetState: () => void;
}

const Develop: React.FC<DevelopProps> = ({ developState, setDevelopState, onGoBack, onResetState }) => {
  const [activeTab, setActiveTab] = useState('commits');

  const {
    repoUrl,
    githubToken,
    repoData,
    commits,
    pulls,
    issues,
    commitAnalysis,
    error,
    isLoading,
  } = developState;

  const handleStateChange = (field: keyof DevelopState, value: any) => {
    setDevelopState(prevState => ({ ...prevState, [field]: value }));
  };

  const handleFetchRepository = useCallback(async () => {
    if (!repoUrl) {
      handleStateChange('error', 'Please enter a GitHub repository URL.');
      return;
    }

    const match = repoUrl.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (!match) {
      handleStateChange('error', 'Invalid GitHub URL. Please use the format: https://github.com/owner/repo');
      return;
    }

    const [, owner, repo] = match;
    handleStateChange('isLoading', true);
    handleStateChange('error', null);
    handleStateChange('commitAnalysis', ''); // Clear previous analysis

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (githubToken) {
      headers['Authorization'] = `Bearer ${githubToken}`;
    }

    try {
      const [repoRes, commitsRes, pullsRes, issuesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/github/repos/${owner}/${repo}`, { headers }),
        fetch(`${API_BASE_URL}/github/repos/${owner}/${repo}/commits`, { headers }),
        fetch(`${API_BASE_URL}/github/repos/${owner}/${repo}/pulls`, { headers }),
        fetch(`${API_BASE_URL}/github/repos/${owner}/${repo}/issues`, { headers }),
      ]);

      // FIX: More robust and user-friendly error handling
      if (!repoRes.ok) {
        const responseText = await repoRes.text().catch(() => 'Could not read error response body.');
        let detailedMessage = '';
        try {
            const errorData = JSON.parse(responseText);
            detailedMessage = errorData.detail || JSON.stringify(errorData);
        } catch (e) {
            detailedMessage = responseText;
        }

        let userFriendlyMessage = '';
        if (repoRes.status === 404) {
            userFriendlyMessage = `Could not access the repository.\n\nThis usually happens for one of two reasons:\n1. The repository is private: Please ensure your GitHub Personal Access Token is correct, has not expired, and has the full 'repo' scope enabled.\n2. The repository URL is incorrect: Please double-check the URL for typos.`;
        } else if (repoRes.status === 401 || repoRes.status === 403) {
            userFriendlyMessage = `Authentication Failed. The provided GitHub Token is invalid, has expired, or does not have sufficient permissions for this repository. Please verify the token and its scopes.`;
        } else {
            userFriendlyMessage = `An unexpected error occurred (Status: ${repoRes.status}).\nDetails: ${detailedMessage}`;
        }
        throw new Error(userFriendlyMessage);
      }

      // Check other responses for completeness
      for (const res of [commitsRes, pullsRes, issuesRes]) {
        if (!res.ok) {
          throw new Error(`Failed to fetch repository data (Status: ${res.status}). The main repository was found, but some details could not be retrieved.`);
        }
      }

      const repoDataJson = await repoRes.json();
      const commitsData = await commitsRes.json();
      const pullsData = await pullsRes.json();
      const issuesData = await issuesRes.json();

      setDevelopState(prevState => ({
        ...prevState,
        repoData: repoDataJson,
        commits: commitsData,
        pulls: pullsData,
        issues: issuesData,
        isLoading: false,
      }));

    } catch (err: any) {
      handleStateChange('error', err.message);
      handleStateChange('isLoading', false);
    }
  }, [repoUrl, githubToken, setDevelopState]);

  const handleAnalyzeCommits = async () => {
    if (commits.length === 0) {
      handleStateChange('error', 'No commits available to analyze.');
      return;
    }
    handleStateChange('isLoading', true);
    handleStateChange('error', null);

    const formData = new FormData();
    formData.append('commits_data', JSON.stringify(commits));

    try {
      const response = await fetch(`${API_BASE_URL}/analyze-commits/`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const responseText = await response.text();
        let errorMessage = `Failed to analyze commits. Status: ${response.status}`;
        try {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.detail || `An unknown error occurred during analysis.`;
        } catch (e) {
            errorMessage = responseText || `An unknown error occurred during analysis.`;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      handleStateChange('commitAnalysis', data.result);
    } catch (err: any) {
      handleStateChange('error', err.message);
    } finally {
      handleStateChange('isLoading', false);
    }
  };

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Develop</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={onGoBack}>
          Back to Home
        </Button>
      </Box>

      {!repoData ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Connect to a GitHub Repository</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Enter the URL of a public or private GitHub repository to analyze its activity. For private repos, a personal access token is required.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="GitHub Repository URL"
                variant="outlined"
                placeholder="https://github.com/owner/repo"
                value={repoUrl}
                onChange={(e) => handleStateChange('repoUrl', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="GitHub Token (Optional)"
                variant="outlined"
                type="password"
                placeholder="Enter your token for private repos"
                value={githubToken}
                onChange={(e) => handleStateChange('githubToken', e.target.value)}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleFetchRepository}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <GitHubIcon />}
            >
              {isLoading ? 'Connecting...' : 'Connect to Repository'}
            </Button>
          </Box>
          {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{error}</Alert>}
        </Paper>
      ) : (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography variant="h5" component="div">
                    <Link href={repoData.html_url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', color: 'inherit' }}>
                      {repoData.full_name}
                    </Link>
                  </Typography>
                  <Typography color="text.secondary">{repoData.description}</Typography>
                </Box>
                <Button onClick={onResetState} size="small">Analyze New Repository</Button>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`Stars: ${repoData.stargazers_count}`} />
                <Chip label={`Forks: ${repoData.forks_count}`} />
                <Chip label={`Open Issues: ${repoData.open_issues_count}`} />
                <Chip label={`Language: ${repoData.language}`} />
              </Box>
            </CardContent>
          </Card>

          <Paper>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} centered>
              <Tab icon={<CommitIcon />} iconPosition="start" label={`Commits (${commits.length})`} value="commits" />
              <Tab icon={<CallSplitIcon />} iconPosition="start" label={`Pull Requests (${pulls.length})`} value="pulls" />
              <Tab icon={<BugReportIcon />} iconPosition="start" label={`Issues (${issues.length})`} value="issues" />
              <Tab icon={<AnalyticsIcon />} iconPosition="start" label="AI Analysis" value="analysis" />
            </Tabs>
            <Divider />

            <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
              {activeTab === 'commits' && (
                <List>
                  {commits.map((commit: any) => (
                    <ListItem key={commit.sha} divider>
                      <ListItemText
                        primary={commit.commit.message}
                        secondary={`by ${commit.commit.author.name} on ${new Date(commit.commit.author.date).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {activeTab === 'pulls' && (
                 <List>
                 {pulls.map((pr: any) => (
                   <ListItem key={pr.id} divider>
                     <ListItemText
                       primary={`#${pr.number} ${pr.title}`}
                       secondary={`Opened by ${pr.user.login} on ${new Date(pr.created_at).toLocaleDateString()}`}
                     />
                     <Chip label={pr.state} color={pr.state === 'open' ? 'success' : 'error'} size="small" />
                   </ListItem>
                 ))}
               </List>
              )}
              {activeTab === 'issues' && (
                <List>
                {issues.map((issue: any) => (
                  <ListItem key={issue.id} divider>
                    <ListItemText
                      primary={`#${issue.number} ${issue.title}`}
                      secondary={`Opened by ${issue.user.login} on ${new Date(issue.created_at).toLocaleDateString()}`}
                    />
                    <Chip label={issue.state} color={issue.state === 'open' ? 'success' : 'error'} size="small" />
                  </ListItem>
                ))}
              </List>
              )}
              {activeTab === 'analysis' && (
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleAnalyzeCommits}
                    disabled={isLoading || commits.length === 0}
                    startIcon={isLoading ? <CircularProgress size={20} /> : <AnalyticsIcon />}
                    sx={{ mb: 2 }}
                  >
                    {commitAnalysis ? 'Re-analyze Commits' : 'Analyze Commits with AI'}
                  </Button>
                  {isLoading && <LinearProgress />}
                  {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
                  {commitAnalysis && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
                      <ThemeAwareMarkdown>{commitAnalysis}</ThemeAwareMarkdown>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Develop;